import { Button, DataTableV2, Modal } from "components/commons";
import useDataTable from "hooks/useDataTable";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { prettifyPaymentMethod } from "utils/pretty.utils";
import { formatPumpNumber } from "utils/text.utils";
import locale from "localization";
import initialState from "./payment-method-transaction-modal.state";
import PaymentMethodTransactionFilter from "./payment-method-transaction-filter";
import styles from "./payment-method-transaction-modal.module.scss";
import { initialShiftRecordFilterState } from "modules/records/shift-records/shift-records-filter.state";
import { nonCashTransactionColumn } from "../noncash-summary-column";
import { nonCashTransactionMapper, getCashAndNonCashTotals } from "../noncash-summary.mapper";
import { dateTimeFormat, escapeCSVValue, downloadCSV, formatDecimal, formatVolume } from "utils";

export default function PaymentMethodTransactionModal({
  paymentMethodTransactions,
  method,
  allTransactions = [],
  transactionsItemDetails = [],
  nonCashTransactions = [],
  onRefetch, // Add refetch callback
  pageMode,
  shift,
  ...modal
}) {
  const [pageState, setPageState] = useState({});
  const [searchKey, setSearchKey] = useState("");
  const [searchType, setSearchType] = useState("transactionNo");
  const filteredTransactions = useMemo(() => {
    let filtered = [...paymentMethodTransactions];

    // Apply search filter
    if (searchKey) {
      const searchValue = searchKey.toLowerCase();

      filtered = filtered.filter((transaction) => {
        if (searchType === "transactionNo") {
          return transaction.transactionNumber?.toLowerCase().includes(searchValue);
        } else if (searchType === "refNumber") {
          return transaction.refNumber?.toLowerCase().includes(searchValue);
        }
        return false;
      });
    }

    return filtered;
  }, [paymentMethodTransactions, searchKey, searchType]);

  const paginatedTransactions = useMemo(() => {
    const { page, perPage } = pageState;
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return filteredTransactions.slice(start, end);
  }, [filteredTransactions, pageState]);

  const { table, filter, search } = useDataTable({
    value: paginatedTransactions || [],
    filter: { initialState: initialShiftRecordFilterState(), mapper: (data) => data },
    table: {
      key: "nonCash",
      name: "nonCashTransactions",
      mapper: (val) =>
        nonCashTransactionMapper({
          val,
          transactionsItemDetails,
          initialState,
          method,
          onRefetch, // Pass refetch to mapper
          pageMode,
        }),
      columns: nonCashTransactionColumn,
    },
  });

  // Override filter and search with custom handlers
  const customFilter = {
    ...filter,
    filterState: {
      ...filter.filterState,
      searchType: searchType,
    },
    modifyFilter: (updates) => {
      if (updates.searchType !== undefined) {
        setSearchType(updates.searchType);
      }
      filter.modifyFilter(updates);
    },
  };

  const customSearch = {
    ...search,
    searchKey: searchKey,
    modifySearch: (value) => {
      setSearchKey(value);
    },
    applySearch: () => {},
    applyClearSearch: () => {
      setSearchKey("");
    },
  };

  useEffect(() => {
    setPageState(table.pageState);
  }, [table.pageState]);

  useEffect(() => {
    setSearchKey("");
    setSearchType("refNumber");
    setPageState({
      page: 1,
      perPage: 10,
    });
  }, [modal.active]);

  const handleExport = useCallback(() => {
    if (!paginatedTransactions.length) return;

    const stationCode = shift?.shift?.stationCode || shift?.station?.stationCode || "";
    const shiftStartDateTime = dateTimeFormat(shift?.shift?.startShift);
    const shiftEndDateTime = dateTimeFormat(shift?.shift?.endShift);
    const shiftNumber = shift?.shift?.shiftNumber || "";

    const csvData = paginatedTransactions.map((transaction) => {
      // Use same logic as nonCashTransactionMapper
      const { transaction: transactionData } = getCashAndNonCashTotals({
        payment: transaction,
        transactionsItemDetails,
      });

      const {
        type,
        quantity,
        name,
        transactionNumber,
        refNumber,
        pumpNumber,
        refName,
        plateNumber,
        remarks,
        totalAmount,
        discount,
        finalAmount,
      } = transaction;

      // Use same computation as nonCashTransactionMapper
      const resultTotalDiscount = discount;
      const hasNegativeSign = resultTotalDiscount < 0;

      // Format quantity same as mapper (2 decimal places for non-fuel)
      let quantityFormat =
        type === "fuel" ? formatVolume(quantity ?? 0) : formatDecimal(quantity, 2);

      // Format pump number same as mapper
      const pumpNo =
        type === "fuel" && pumpNumber ? formatPumpNumber(pumpNumber.split(",")[0]) : "--";

      // Format ref name / plate number same as mapper (separate values)
      let reference = refName;
      if (reference === "") {
        reference = "--";
      }

      let plateNum = plateNumber;
      if (plateNum === "") {
        plateNum = "--";
      }

      // Get remarks from multiple possible sources same as mapper
      const transactionRemarks = remarks ?? (transactionData?.remarks || "--");

      return {
        "Station Code": escapeCSVValue(stationCode) || "--",
        "Shift Start Date & Time": escapeCSVValue(shiftStartDateTime) || "--",
        "Shift End Date & Time": escapeCSVValue(shiftEndDateTime) || "--",
        "Reference No.": escapeCSVValue(refNumber) || "--",
        Amount: Number(totalAmount || 0).toFixed(2),
        "Discount/Premium": `${hasNegativeSign ? "" : resultTotalDiscount > 0 ? "-" : ""}${Math.abs(resultTotalDiscount || 0).toFixed(2)}`,
        "Final Amount Paid": Number(finalAmount || 0).toFixed(2),
        // Excess: excessAmount < 0.005 ? "--" : escapeCSVValue(formatAmountNoPrefix(excessAmount)),
        "Transaction No.": escapeCSVValue(transactionNumber) || "--",
        "Product Name": escapeCSVValue(name) || "--",
        "Pump No.": escapeCSVValue(pumpNo),
        Quantity: quantityFormat || 0,
        "Ref. Name": escapeCSVValue(reference) || "--",
        "Plate Number": escapeCSVValue(plateNum) || "--",
        Remarks: escapeCSVValue(transactionRemarks),
      };
    });

    const filename = `${stationCode}-${shiftNumber}-${prettifyPaymentMethod(
      method,
    ).toLocaleUpperCase()}.csv`;
    downloadCSV(csvData, filename);
  }, [paginatedTransactions, shift, method, transactionsItemDetails]);

  return (
    <Modal
      {...modal}
      // title={prettifyPaymentMethod(method)}
      className={styles.modal}
      titleBlack
      headerExtra={
        <PaymentMethodTransactionFilter
          title={prettifyPaymentMethod(method)}
          {...customFilter}
          {...customSearch}
          exportReport={handleExport}
          exporting={false}
        />
      }
      textHeaderStyle={styles.textHeader}
      headerStyle={styles.header}
    >
      <DataTableV2
        {...table}
        result={{
          nonCash: paginatedTransactions,
        }}
        totalCount={filteredTransactions.length}
        refetch={() => {}}
        hideNoSearchResultRender
        tableVersion="v2"
        styleV2={true}
        size="small"
        childBottom={
          <Button onClick={() => modal?.close?.()} style={{ marginTop: "1.5em" }} outline>
            {locale.back}
          </Button>
        }
      />
    </Modal>
  );
}
